/**
 * HOST TO SURFACE ROUTING - which half of the app a hostname is allowed to serve.
 *
 * His decision, 2026-09-20 (93rd): `portal.apex-res.com` is the CLIENT host. It serves client
 * links and nothing else; any staff path is handed to `app.apex-res.com` at the SAME path, and a
 * trimmed URL gets a neutral client page instead of the Apex staff login.
 *
 * ⭐⭐⭐ WHY THIS IS IN THE APP AND NOT IN `vercel.json`. A host-conditional rewrite is real -
 * `has: [{ type: 'host', value }]` is in Vercel's Build Output API `HasField`, and the
 * `{ type: 'header', key: 'host' }` spelling is documented for `vercel.json` - but it CANNOT gate
 * this app. `frontend/vercel.json` already rewrites `/(.*)` to `/index.html`; once that single
 * bundle boots, React Router owns every navigation and the origin server never sees another
 * request. A rewrite chooses WHICH FILE to serve and there is only one. Gating an SPA is a
 * boot-time decision, so it lives here, in ONE place.
 * ⛔ Resist restating this policy anywhere else. The 92nd cost a whole feature to a schema written
 * in three places with nothing keeping them in agreement.
 *
 * ⛔⛔ GATING IS OPT-IN, NEVER OPT-OUT. Only a host listed in `GATED_CLIENT_HOSTS` is restricted.
 * Every other hostname - `app.apex-res.com`, `lomacre.com`, `apex-res.com`, `localhost`, a
 * `*.vercel.app` preview, anything unrecognised - serves the whole app. This is the CORS ruling of
 * the 92nd applied one layer up: known hosts are hard-coded, and failing CLOSED on an unknown host
 * is a total outage. An unknown host losing the gate is a missing restriction; an unknown host
 * losing the APP is Joseph unable to work.
 */

/** The staff origin a gated host hands staff paths to. */
export const STAFF_ORIGIN = 'https://app.apex-res.com';

/**
 * The origin every PRIVATE CLIENT LINK is built on, whatever host minted it.
 *
 * His decision, 2026-09-20: *"I want us to be on the app, because it's our app for Apex, but I want
 * that portal link for the clients to go to portal dot Apex."* Staff work on `app.apex-res.com`;
 * what a client receives reads `portal.apex-res.com`.
 *
 * ⛔⛔ **A LINK YOU SEND SOMEONE MUST NOT DEPEND ON WHICH HOST YOU HAPPENED TO BE STANDING ON.**
 * Every one of these was `window.location.origin`, which was survivable only while staff and
 * clients shared a host. The gate ended that: the admin pages now live on `app.apex-res.com`, so a
 * minted link inherited the STAFF host and read as an internal tool to the client it was sent to.
 *
 * ⛔ PRIVATE client links only - `/portal/:token` and `/deal-room/:token`, each sent to a named
 * counterparty. **`/listing/:slug` is deliberately NOT routed here**: it is public marketing, and
 * this host tells a visitor it serves "the secure document links we send to clients". Which origin
 * a public listing should carry is his call, still open.
 *
 * ⭐ One constant, so the LomaCRE rename is a one-line change rather than a hunt.
 */
export const CLIENT_LINK_ORIGIN = 'https://portal.apex-res.com';

/**
 * The origin every PUBLIC MARKETING link is built on. His call, 2026-09-20: *"put the listing links
 * on lomacre.com."*
 *
 * ⭐ WHY A SECOND ORIGIN RATHER THAN REUSING THE CLIENT ONE. A listing site is a shareable marketing
 * page - it goes in a LoopNet post, a marketing email, a text to a prospect - and it carries no
 * token: anyone with the URL can open it. `portal.apex-res.com` tells a visitor it serves "the
 * secure document links we send to clients", so a public listing does not belong there.
 * `app.apex-res.com` is staff, and `apex-res.com` is the WordPress site, which does not serve this
 * SPA at all. **`lomacre.com` is the product he advertises from, which is exactly what a listing is.**
 *
 * ⚠️ `lomacre.com` 308-redirects to `www.lomacre.com` and **the redirect PRESERVES THE PATH**
 * (measured: `lomacre.com/listing/example-slug` lands on `www.lomacre.com/listing/example-slug`,
 * HTTP 200). The bare form is kept because it is the prettier thing to paste; a link merely takes
 * one redirect hop. Switch this constant to the `www.` form to skip the hop.
 */
export const MARKETING_LINK_ORIGIN = 'https://lomacre.com';

/**
 * Build a private client link. `path` is root-relative and keeps its leading slash.
 *
 * ⚠️ It is absolute ON PURPOSE, even in local dev. The token it carries was minted against the
 * production database (`frontend/.env.local` points local dev at production), so a `localhost` or
 * `*.vercel.app` link would be the one thing that genuinely does not work for the client.
 */
export function clientLinkUrl(path: string): string {
  return absoluteLink(CLIENT_LINK_ORIGIN, path);
}

/**
 * Build a public marketing link - today, a listing site. Same rule as `clientLinkUrl`: absolute, so
 * it never inherits whichever host the staff member happened to be standing on.
 */
export function marketingLinkUrl(path: string): string {
  return absoluteLink(MARKETING_LINK_ORIGIN, path);
}

function absoluteLink(origin: string, path: string): string {
  return `${origin}${path.startsWith('/') ? path : `/${path}`}`;
}

/**
 * Hosts restricted to the client surface. Lower-case, no port - `location.hostname` never carries
 * one. ⛔ Adding a host here REMOVES the staff app from it; that is his call, not a tidy-up.
 */
export const GATED_CLIENT_HOSTS: readonly string[] = ['portal.apex-res.com'];

/**
 * Route prefixes a gated client host serves, MEASURED from `App.tsx` rather than taken from a note
 * (the note said four public routes; the router declares six).
 *
 * 🚨🚨 EVERY PREFIX KEEPS ITS TRAILING SLASH, AND THAT IS LOAD-BEARING. Two STAFF routes are
 * prefixes of two client ones: `/portals` (PortalsAdmin) against `/portal/:token`, and
 * `/listing-sites` against `/listing/:slug`. Drop a slash and this file hands a client the portal
 * ADMIN screen. `hostPolicy.test.ts` pins both.
 *
 * ⛔ `/login` and `/signup` are public to the ROUTER but they are STAFF entry points, so they are
 * deliberately absent - they hand off. "Public route" and "client route" are not the same set.
 */
export const CLIENT_ROUTE_PREFIXES: readonly string[] = [
  '/listing/',
  '/deal-room/',
  '/escrow/',
  '/portal/',
];

export type HostDecision =
  /** Render the app as normal. */
  | { action: 'serve' }
  /** Render the neutral client landing - a trimmed URL on a client host. */
  | { action: 'client-home' }
  /** Leave for the staff origin at the same path. */
  | { action: 'handoff'; to: string };

/** True when this hostname is restricted to the client surface. */
export function isGatedClientHost(hostname: string): boolean {
  return GATED_CLIENT_HOSTS.includes(hostname.trim().toLowerCase());
}

/** True when this path is one a client link legitimately reaches. */
export function isClientRoute(pathname: string): boolean {
  return CLIENT_ROUTE_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

/**
 * The whole policy. Pure: it takes the location apart rather than reading `window`, so a test can
 * state a hostname without a DOM.
 *
 * `search` and `hash` are carried through the handoff verbatim. A staff member who lands on the
 * client host mid-task keeps their place, and a link with a query survives.
 */
export function decideSurface(
  hostname: string,
  pathname: string,
  search = '',
  hash = '',
): HostDecision {
  if (!isGatedClientHost(hostname)) return { action: 'serve' };
  if (isClientRoute(pathname)) return { action: 'serve' };
  if (pathname === '/' || pathname === '') return { action: 'client-home' };
  return { action: 'handoff', to: `${STAFF_ORIGIN}${pathname}${search}${hash}` };
}
